import { DateRangePicker, DatePreset } from '@/components/ui/DateRangePicker';
import { cn } from '@/utils/cn';
import type { RTPGrouping } from '@/types/rtpDownload';

export interface RTPFilterFormProps {
  timeRange: { from: string | null; to: string | null };
  grouping: RTPGrouping;
  onTimeRangeChange: (from: string | null, to: string | null) => void;
  onGroupingChange: (grouping: RTPGrouping) => void;
  className?: string;
}

// Quick time presets for RTP data
const getLastMonth = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  return {
    from: lastMonth.toISOString().slice(0, 16),
    to: lastMonthEnd.toISOString().slice(0, 16),
  };
};

const TIME_PRESETS: DatePreset[] = [
  {
    label: 'Last hour',
    from: new Date(Date.now() - 60 * 60 * 1000).toISOString().slice(0, 16),
    to: new Date().toISOString().slice(0, 16),
  },
  {
    label: 'Last 6 hours',
    from: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString().slice(0, 16),
    to: new Date().toISOString().slice(0, 16),
  },
  {
    label: 'Last 24 hours',
    from: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    to: new Date().toISOString().slice(0, 16),
  },
  {
    label: 'Last week',
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    to: new Date().toISOString().slice(0, 16),
  },
  {
    label: 'Last 30 days',
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    to: new Date().toISOString().slice(0, 16),
  },
  {
    label: 'Last month',
    ...getLastMonth(),
  },
];

const GROUPING_OPTIONS: Array<{ value: RTPGrouping; label: string }> = [
  { value: 'hour', label: 'Per Hour' },
  { value: 'day', label: 'Per Day' },
  { value: 'week', label: 'Per Week' },
  { value: 'month', label: 'Per Month' }
];

export function RTPFilterForm({
  timeRange,
  grouping,
  onTimeRangeChange,
  onGroupingChange,
  className,
}: RTPFilterFormProps) {
  return (
    <div className={className}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Date Range Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">
            Date Range
          </label>
          <DateRangePicker
            from={timeRange.from}
            to={timeRange.to}
            onChange={onTimeRangeChange}
            includeTime={true}
            presets={TIME_PRESETS}
            testIdPrefix="rtp-date-range"
          />
          <p className="mt-1.5 text-xs text-muted">
            Select the time period for RTP data download
          </p>
        </div>

        {/* Grouping Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-foreground">
            Group By
          </label>
          <div className="flex flex-wrap gap-2">
            {GROUPING_OPTIONS.map((option) => {
              const isSelected = grouping === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onGroupingChange(option.value)}
                  data-testid={`rtp-grouping-${option.value}`}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm font-medium transition-all',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    isSelected
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-xs text-muted">
            Choose how to aggregate RTP data points over time
          </p>
        </div>
      </div>
    </div>
  );
}
